import { useNavigate, useSearchParams } from "react-router-dom";

import Swal from "sweetalert2";

import DashboardLayout from "../../../components/layout/DashboardLayout";

import { useWeightTestViewModel } from "../../../viewmodels/operation/weighttest/useWeightTestViewModel";

const WeightTestsPage = () => {
  const navigate = useNavigate();

  const [, setSearchParams] = useSearchParams();

  const {
    weightTests,
    loading,

    page,
    setPage,
    search,
    setSearch,

    deleteWeightTest,
  } = useWeightTestViewModel();

  const changePage = (newPage: number) => {
    setPage(newPage);

    setSearchParams({
      page: String(newPage),
      search,
    });
  };

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "Delete Weight Test?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await deleteWeightTest(id);

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Weight test deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch {
      await Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: "Unable to delete weight test.",
      });
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
          <div>
            <h1 className="text-2xl font-bold">Weight Tests</h1>

            <p className="text-gray-500 mt-1">Manage weight tests</p>
          </div>

          <button
            onClick={() => navigate("/weight-test-reports/create")}
            className="bg-primary text-white px-5 py-3 rounded-2xl font-medium hover:opacity-90 transition"
          >
            + Create
          </button>
        </div>

        {/* SEARCH */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-5">
           <label className="block text-sm font-medium mb-2">Search</label>
          <div>
            <input
              value={search}
              onChange={(e) => {
                const value = e.target.value;

                setSearch(value);
                setPage(1);

                setSearchParams({
                  page: "1",
                  search: value,
                });
              }}
              placeholder="Customer name..."
              className="w-full border border-slate-300 rounded-xl px-4 py-3 outline-none focus:border-primary"
            />
          </div>
        </div>

        {/* TABLE */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="w-full overflow-x-auto md:overflow-x-visible">
            <table className="w-full md:min-w-0 min-w-[1100px] text-sm text-left table-auto">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    Form No
                  </th>

                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    Customer
                  </th>

                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    Address
                  </th>

                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    Date
                  </th>

                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    Due Date
                  </th>

                  {/*  <th className="p-3 border border-slate-200 whitespace-nowrap">
                      Printed
                    </th> */}

                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center p-8 border border-slate-200"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : weightTests.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center p-8 border border-slate-200 text-gray-500"
                    >
                      No records found
                    </td>
                  </tr>
                ) : (
                  weightTests.map((x: any) => (
                    <tr key={x.id} className="hover:bg-slate-50 transition">
                      {/* FORM NO */}
                      <td className="p-3 border border-slate-200 font-semibold whitespace-nowrap">
                        {x.formNo}
                      </td>

                      {/* CUSTOMER */}
                      <td className="p-3 border border-slate-200">
                        <div className="font-medium break-words">
                          {x.customerName}
                        </div>
                      </td>

                      {/* ADDRESS */}
                      <td className="p-3 border border-slate-200 break-words">
                        {x.customerAddress}
                      </td>

                      {/* DATE */}
                      <td className="p-3 border border-slate-200 whitespace-nowrap">
                        {x.date
                          ? new Date(x.date).toLocaleDateString("en-GB")
                          : "-"}
                      </td>

                      {/* DUE DATE */}
                      <td className="p-3 border border-slate-200 whitespace-nowrap">
                        {x.dueDate
                          ? new Date(x.dueDate).toLocaleDateString("en-GB")
                          : "-"}
                      </td>

                      {/* PRINTED */}
                      {/* <td className="p-3 border border-slate-200 whitespace-nowrap">

                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                x.isPrinted === 1
                                  ? "bg-green-100 text-green-700"
                                  : "bg-yellow-100 text-yellow-700"
                              }`}
                            >

                              {x.isPrinted === 1
                                ? "Printed"
                                : "Not Printed"}

                            </span>

                          </td> */}

                      {/* ACTIONS */}
                      <td className="p-3 border border-slate-200 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {/* VIEW */}
                          <button
                            onClick={() =>
                              navigate(
                                `/weight-test-reports/view/${x.id}?page=${page}&search=${encodeURIComponent(search)}`,
                              )
                            }
                            className="bg-blue-500 text-white px-3 py-2 rounded-lg text-sm"
                          >
                            View
                          </button>

                          {/* EDIT */}
                          <button
                            onClick={() =>
                              navigate(
                                `/weight-test-reports/edit/${x.id}?page=${page}&search=${encodeURIComponent(search)}`,
                              )
                            }
                            className="bg-yellow-500 text-white px-3 py-2 rounded-lg text-sm"
                          >
                            Edit
                          </button>

                          {/* DELETE */}
                          <button
                            onClick={() => handleDelete(x.id)}
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm transition"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PAGINATION */}
        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            disabled={page === 1}
            onClick={() => changePage(Math.max(1, page - 1))}
            className="px-4 py-2 border rounded-xl disabled:opacity-50"
          >
            Prev
          </button>

          <span className="text-sm font-medium">Page {page}</span>

          <button
            onClick={() => changePage(page + 1)}
            className="px-4 py-2 border rounded-xl"
          >
            Next
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WeightTestsPage;
